/**
 * useRailWidth — the full-screen rail's width, and the gesture that changes it
 * (owner ask, Miles 2026-09-20: "the dividing line should be customizable from
 * left to right and be sticky once the user lands on it").
 *
 * WHY THE DRAG IS IMPERATIVE. The rail's width is `ChatContainer`'s
 * `padding-left`, and ChatContainer is the `chat-pane` CSS container. Writing
 * the width through React state would re-render an eleven-thousand-line
 * component on every pointer frame; writing it through the element's own style
 * property does not re-render anything at all. So the gesture:
 *
 *   • sets the custom property (and the separator's aria-valuenow) directly on
 *     the DOM during the move,
 *   • stamps `data-rail-resizing` so the stylesheet can drop transitions and
 *     the page can stop selecting text,
 *   • SUSPENDS the pane-width observer (useElementContentWidth's second
 *     argument), which would otherwise re-render the host on every frame by
 *     itself, and
 *   • commits to React state + localStorage exactly once, on release.
 *
 * NO STORED CHOICE ⇒ NO INLINE OVERRIDE. The property is removed rather than
 * written, so the stylesheet's own `clamp(264px, 20vw, 360px)` serves and the
 * default keeps tracking a window resize with no JS in the loop. A stored
 * choice is re-clamped for DISPLAY on a viewport change but never rewritten —
 * a width chosen on a large screen survives a trip through a small window
 * (`clampWindowRect`'s "keep it recoverable" rule, windowGeometry.ts).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RAIL_WIDTH_VAR,
  clampRailWidth,
  defaultRailWidthPx,
  railWidthBounds,
  resolveRailWidth,
} from './railWidth';
import { GLOBAL_CHAT_RAIL_WIDTH_KEY } from './types';
import { getFromLocalStorage, removeFromLocalStorage, setToLocalStorage } from './utils';

/** One arrow press. A nudge you can feel, small enough to aim with. */
export const RAIL_KEYBOARD_STEP_PX = 16;

/** Marks the container mid-gesture: transitions off, no text selection. */
export const RAIL_RESIZING_ATTR = 'data-rail-resizing';

export interface RailWidthApi {
  /** The committed width, or null while the screen-aware default serves. */
  width: number | null;
  /** True between pointerdown and pointerup — feeds the observer's suspend. */
  resizing: boolean;
  /**
   * Bounds for the separator's aria-valuemin/max, at THIS viewport — a
   * splitter that announces a maximum it will not accept is lying to the one
   * person who cannot see where it stopped.
   */
  min: number;
  max: number;
  /** What the separator currently reports — never null, so aria is honest. */
  valueNow: number;
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  /** Double-click the divider to go back to the screen-aware default. */
  onDoubleClick: () => void;
}

function viewportWidth(): number {
  return typeof window === 'undefined' ? 0 : window.innerWidth;
}

function readStored(): number | null {
  const raw = getFromLocalStorage<number | null>(GLOBAL_CHAT_RAIL_WIDTH_KEY, null);
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

/**
 * @param containerRef  the ChatContainer element that owns the custom property
 * @param active        the rail is on screen (fullscreen, wide enough)
 */
export function useRailWidth(
  containerRef: { current: HTMLElement | null },
  active: boolean,
): RailWidthApi {
  const [width, setWidth] = useState<number | null>(() => readStored());
  const [resizing, setResizing] = useState(false);
  // The live value during a gesture, so a pointermove never reads state.
  const liveRef = useRef<number | null>(null);
  /**
   * How to abandon a gesture that is still in flight. Installed on
   * pointerdown, cleared when it ends — and called on unmount, because a pane
   * that disappears mid-drag (a mode change, a sign-out) cannot run its own
   * pointerup, and would otherwise leave the Escape listener on the window and
   * the page unselectable.
   */
  const abandonGestureRef = useRef<(() => void) | null>(null);
  const separatorRef = useRef<HTMLElement | null>(null);

  /** Write the property (or clear it) plus the separator's spoken value. */
  const paint = useCallback((px: number | null) => {
    const el = containerRef.current;
    if (el) {
      if (px === null) el.style.removeProperty(RAIL_WIDTH_VAR);
      else el.style.setProperty(RAIL_WIDTH_VAR, `${px}px`);
    }
    const sep = separatorRef.current;
    if (sep) {
      const spoken = px === null ? Math.round(defaultRailWidthPx(viewportWidth())) : px;
      sep.setAttribute('aria-valuenow', String(spoken));
    }
  }, [containerRef]);

  // Apply the committed width whenever it, or the rail's presence, changes.
  // A stored value is re-clamped against the CURRENT viewport for display; the
  // stored number itself is left alone (see the header).
  useEffect(() => {
    if (!active) {
      containerRef.current?.style.removeProperty(RAIL_WIDTH_VAR);
      return;
    }
    paint(resolveRailWidth(width, viewportWidth()));
  }, [active, width, paint, containerRef]);

  // A window resize can invalidate a stored width (the ceiling is viewport
  // aware). Re-paint, never re-store.
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;
    const onResize = () => {
      if (liveRef.current !== null) return; // a gesture owns the property
      paint(resolveRailWidth(width, viewportWidth()));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, width, paint]);

  const commit = useCallback((px: number | null) => {
    liveRef.current = null;
    setWidth(px);
    if (px === null) removeFromLocalStorage(GLOBAL_CHAT_RAIL_WIDTH_KEY);
    else setToLocalStorage(GLOBAL_CHAT_RAIL_WIDTH_KEY, px);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const container = containerRef.current;
    if (!container) return;
    e.preventDefault();
    const target = e.currentTarget;
    separatorRef.current = target;
    try { target.setPointerCapture(e.pointerId); } catch { /* older engine: move events still arrive */ }

    const originX = e.clientX;
    const originWidth = resolveRailWidth(width, viewportWidth())
      ?? Math.round(defaultRailWidthPx(viewportWidth()));
    liveRef.current = originWidth;
    // A press that never moved is NOT a choice. Committing the origin on a
    // bare click would freeze a rail that had been following the viewport
    // into a fixed pixel width — and it would make the double-click reset
    // impossible, because the two clicks inside it would each re-store the
    // very width the reset exists to discard.
    let moved = false;
    setResizing(true);
    container.setAttribute(RAIL_RESIZING_ATTR, 'true');

    const move = (ev: PointerEvent) => {
      const next = clampRailWidth(originWidth + (ev.clientX - originX), viewportWidth());
      if (next === liveRef.current) return;
      moved = true;
      liveRef.current = next;
      paint(next);
    };
    /** @param keep false ⇒ Escape or a cancelled pointer: put it back. */
    const end = (keep: boolean) => {
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onCancel);
      if (typeof window !== 'undefined') window.removeEventListener('keydown', onEscape, true);
      abandonGestureRef.current = null;
      try { target.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      container.removeAttribute(RAIL_RESIZING_ATTR);
      setResizing(false);
      const final = liveRef.current;
      if (keep && moved && final !== null) {
        commit(final);
      } else {
        // Nothing to record. Repaint whatever the committed state says, so an
        // abandoned gesture leaves no trace on the DOM either.
        liveRef.current = null;
        paint(resolveRailWidth(width, viewportWidth()));
      }
    };
    const onUp = () => end(true);
    const onCancel = () => end(false);
    abandonGestureRef.current = onCancel;
    const onEscape = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      end(false);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onCancel);
    if (typeof window !== 'undefined') window.addEventListener('keydown', onEscape, true);
  }, [containerRef, width, paint, commit]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    separatorRef.current = e.currentTarget;
    const vw = viewportWidth();
    const current = resolveRailWidth(width, vw) ?? Math.round(defaultRailWidthPx(vw));
    let next = current;
    const bounds = railWidthBounds(vw);
    if (e.key === 'ArrowLeft') next = current - RAIL_KEYBOARD_STEP_PX;
    else if (e.key === 'ArrowRight') next = current + RAIL_KEYBOARD_STEP_PX;
    else if (e.key === 'Home') next = bounds.min;
    else next = bounds.max;
    commit(clampRailWidth(next, vw));
  }, [width, commit]);

  useEffect(() => () => { abandonGestureRef.current?.(); }, []);

  const onDoubleClick = useCallback(() => { commit(null); }, [commit]);


  const valueNow = resolveRailWidth(width, viewportWidth())
    ?? Math.round(defaultRailWidthPx(viewportWidth()));

  const bounds = railWidthBounds(viewportWidth());

  return {
    width,
    resizing,
    min: bounds.min,
    max: bounds.max,
    valueNow,
    onPointerDown,
    onKeyDown,
    onDoubleClick,
  };
}
